import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ConfirmationDialog } from '@roger/ui-components';
import {
  updatePatient,
  checkPatientOasisMrnSync,
  syncPatientOasisMrn,
  checkPatientOasisExportErrors,
} from '../../services/api';
import type {
  PatientResponse,
  PatientUpdate,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { formatDateToISO } from '../../utils/dateUtils';
import { AutoSavePill } from '../AutoSavePill';
import { CHART_AUTOSAVE_DELAY_MS } from '../../hooks/useAutoSave';
import { useCmsRecordCheck } from '../../hooks/useCmsRecordCheck';
import { cmsErrorsByField } from '../oasis/cmsFieldErrors';
import {
  formsDiffer,
  getChangedMrnUpdate,
  getChangedPhoneUpdates,
  hasValidChangedMrn,
  hasValidChangedPhones,
  isPhoneField,
  patientToForm,
  type PatientInfoFormState,
  type PhoneField,
  type UpdatePatientInfoField,
} from './patientInfoForm';
import { PatientDemographicSection } from './PatientDemographicSection';
import { PatientContactSection } from './PatientContactSection';
import { PatientEmergencyContactsSection } from './PatientEmergencyContactsSection';
import { PatientEpisodeFlagsSection } from './PatientEpisodeFlagsSection';
import { PatientTagsSection } from './PatientTagsSection';

interface PatientInfoTabProps {
  patient: PatientResponse;
  canEditPatientInfo: boolean;
  onPatientUpdated: (patient: PatientResponse) => void;
  onEpisodeAssignmentsChanged?: () => void;
}

type PendingSave = {
  patientId: string;
  form: PatientInfoFormState;
  // Revert-to-saved is not dirty vs lastSavedFormRef, but must still PUT so an
  // in-flight write of the abandoned value does not win in the database.
  force?: boolean;
  // A phone edit made while a PUT is in flight remains an explicit intent even
  // when the final value equals the saved baseline (for example, type then
  // clear). This keeps the queued PUT from omitting a required null.
  phoneFieldsChangedWhileSaving?: PhoneField[];
  // Same reason as the phone fields above: an MRN typed and then reverted
  // while a PUT was in flight is still intent to write, even though the
  // final value equals the baseline again.
  mrnChangedWhileSaving?: boolean;
};

function takePendingSave(ref: {
  current: PendingSave | null;
}): PendingSave | null {
  const queued = ref.current;
  ref.current = null;
  return queued;
}

// Signatures are mentioned only when there are some. Of the statuses that are
// still editable, only a chart QA is mid-correction on can carry one, so the
// count is usually zero and naming it every time would suggest otherwise.
function m0020SyncMessage(
  sync: { visitIds: string[]; signatureCount: number } | null
): string {
  if (!sync) return '';
  const visits = sync.visitIds.length;
  const visitText = `${visits} ${visits === 1 ? 'visit' : 'visits'}`;
  const base = `The OASIS Patient ID (M0020) still shows the previous MRN on ${visitText}. Update ${visits === 1 ? 'it' : 'them'} to the new MRN?`;
  if (sync.signatureCount === 0) return base;
  const signatures = `${sync.signatureCount} ${
    sync.signatureCount === 1 ? 'signature' : 'signatures'
  }`;
  return `${base} This will invalidate ${signatures} that must then be re-signed.`;
}

// Composition root for the Patient Info tab. Owns the patient form state and
// its debounced autosave (including the pending-save queue, flush on unmount,
// and the parent-echo rules) and the OASIS M0020 follow-up that autosave can
// trigger. The field grids live in the section components; the current
// episode assignments save on their own and live in
// PatientEpisodeFlagsSection.
export const PatientInfoTab: React.FC<PatientInfoTabProps> = ({
  patient,
  canEditPatientInfo,
  onPatientUpdated,
  onEpisodeAssignmentsChanged,
}) => {
  const { error: showError, success: showSuccess } = useToast();
  const [form, setForm] = useState<PatientInfoFormState>(() =>
    patientToForm(patient)
  );
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'unsaved' | 'saving' | 'saved' | 'error'
  >('idle');
  // Bumps on each debounced edit so the pill restarts its countdown bar.
  const [changeToken, setChangeToken] = useState(0);

  // Charts still answering OASIS M0020 with the previous MRN, pending the
  // "also update them?" popup. Non-null means the popup is open. The MRN the
  // user was shown travels with it and is sent on confirm, so if the patient's
  // MRN has moved again in the meantime the server refuses rather than writing
  // a value nobody agreed to.
  const [m0020Sync, setM0020Sync] = useState<{
    mrn: string;
    visitIds: string[];
    signatureCount: number;
  } | null>(null);
  const [isSyncingM0020, setIsSyncingM0020] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFormRef = useRef(form);
  latestFormRef.current = form;
  const isMountRef = useRef(true);
  // Skip the debounce effect when we apply a current-generation save echo;
  // that setForm is not a user edit.
  const skipAutosaveRef = useRef(false);
  // Dirty checks compare against this, not the parent `patient` prop: the
  // parent can lag (or canonicalize) without meaning the user has a new edit.
  const lastSavedFormRef = useRef(patientToForm(patient));
  const patientIdRef = useRef(patient.id);
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef<PendingSave | null>(null);
  // Keep phone edits made during an in-flight PUT as explicit queued intent.
  // Comparing only with lastSavedFormRef loses type-then-clear because the
  // cleared value can equal the baseline again.
  const phoneFieldsChangedWhileSavingRef = useRef<Set<PhoneField>>(new Set());
  const mrnChangedWhileSavingRef = useRef(false);
  // Bumps on every user edit, including a revert, so a stale in-flight
  // completion cannot report "saved" over newer intent.
  const editGenerationRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    const next = patientToForm(patient);
    if (patient.id !== patientIdRef.current) {
      setForm(next);
      lastSavedFormRef.current = next;
      patientIdRef.current = patient.id;
      pendingSaveRef.current = null;
      phoneFieldsChangedWhileSavingRef.current.clear();
      mrnChangedWhileSavingRef.current = false;
      // The offer belongs to the patient it was made about. Left open across a
      // navigation it would name the previous chart's visits while pointing at
      // this one, which the server refuses but the user should never be asked.
      setM0020Sync(null);
      isMountRef.current = true;
      return;
    }
    // Same patient: adopt a parent echo only when the form is clean.
    // Dirty forms ignore it so a save snapshot cannot wipe in-progress
    // typing (ticket 28175 / EMR-1452). Clean forms must adopt, or a
    // remount after tab-switch hydrates a stale parent and the next
    // autosave overwrites the value that already made it to the server.
    if (formsDiffer(latestFormRef.current, lastSavedFormRef.current)) {
      return;
    }
    if (!formsDiffer(latestFormRef.current, next)) {
      return;
    }
    lastSavedFormRef.current = next;
    skipAutosaveRef.current = true;
    setForm(next);
  }, [patient]);

  const isDirty = useCallback((current: PatientInfoFormState) => {
    return formsDiffer(current, lastSavedFormRef.current);
  }, []);

  // Ask the server which charts the MRN change left behind. A failure here is
  // swallowed on purpose: the MRN itself already saved, and a popup that never
  // appears is a smaller problem than an error toast about a chart the user
  // was not asking about.
  const offerM0020Sync = useCallback(async (patientId: string, mrn: string) => {
    try {
      const check = await checkPatientOasisMrnSync({
        path: { patient_id: patientId },
      });
      const visitIds = check.data?.visit_ids ?? [];
      if (!visitIds.length || !mountedRef.current) return;
      if (patientIdRef.current !== patientId) return;
      setM0020Sync({
        mrn,
        visitIds,
        signatureCount: check.data?.signature_count ?? 0,
      });
    } catch (err: unknown) {
      console.error('Failed to check OASIS Patient ID sync:', err);
    }
  }, []);

  const handleConfirmM0020Sync = async () => {
    if (!m0020Sync) return;
    setIsSyncingM0020(true);
    try {
      const response = await syncPatientOasisMrn({
        path: { patient_id: patientIdRef.current },
        body: { visit_ids: m0020Sync.visitIds, mrn: m0020Sync.mrn },
      });
      const updated = response.data?.updated_visit_ids.length ?? 0;
      if (response.error || !response.data) {
        console.error('Failed to update OASIS Patient ID:', response.error);
        // A 409 here means the MRN moved again while the popup was open, and
        // saying so is more use than a generic failure.
        showError(
          getUserFacingApiMessage(
            response,
            'Failed to update the OASIS Patient ID'
          )
        );
      } else if (updated > 0) {
        showSuccess(
          `OASIS Patient ID updated on ${updated} ${
            updated === 1 ? 'visit' : 'visits'
          }`
        );
      } else {
        // Everything offered has since been signed, completed, or had its
        // period closed by someone else.
        showSuccess('Those visits can no longer be edited, so none changed');
      }
    } catch (err: unknown) {
      console.error('Failed to update OASIS Patient ID:', err);
      showError('Failed to update the OASIS Patient ID');
    } finally {
      setIsSyncingM0020(false);
      setM0020Sync(null);
    }
  };

  // Returns a finished save's status to idle. Tracked in a ref so the next one
  // replaces it and unmount can cancel it before it touches a torn-down tree.
  const scheduleStatusReset = useCallback(
    (from: 'saved' | 'error', ms: number) => {
      if (statusResetTimer.current) clearTimeout(statusResetTimer.current);
      statusResetTimer.current = setTimeout(() => {
        statusResetTimer.current = null;
        setSaveStatus(s => (s === from ? 'idle' : s));
      }, ms);
    },
    []
  );

  const doSave = useCallback(
    async (
      formSnapshot: PatientInfoFormState,
      opts?: Omit<PendingSave, 'form'>
    ) => {
      if (!canEditPatientInfo) return;
      if (!formSnapshot.first_name.trim() || !formSnapshot.last_name.trim()) {
        return;
      }
      if (!hasValidChangedPhones(formSnapshot, lastSavedFormRef.current)) {
        return;
      }
      if (!hasValidChangedMrn(formSnapshot, lastSavedFormRef.current)) {
        return;
      }
      if (!opts?.force && !isDirty(formSnapshot)) return;

      const targetPatientId = opts?.patientId ?? patientIdRef.current;

      // One in-flight PUT at a time. Keep only the latest snapshot so an
      // older request cannot commit after a newer one (last-write-wins).
      // The queued intent is bound to the patient it belongs to, so draining
      // after a navigation cannot write B's form onto A's id.
      if (isSavingRef.current) {
        pendingSaveRef.current = {
          patientId: targetPatientId,
          form: formSnapshot,
          force: opts?.force,
          phoneFieldsChangedWhileSaving: Array.from(
            phoneFieldsChangedWhileSavingRef.current
          ),
          mrnChangedWhileSaving: mrnChangedWhileSavingRef.current,
        };
        return;
      }

      const savingGeneration = editGenerationRef.current;
      const savingPatientId = targetPatientId;
      isSavingRef.current = true;
      // Any phone edits accumulated since the previous request began belong to
      // this queued snapshot. New edits will be tracked for the next request.
      const explicitlyChangedPhoneFields =
        opts?.phoneFieldsChangedWhileSaving ?? [];
      const explicitlyChangedMrn = opts?.mrnChangedWhileSaving ?? false;
      phoneFieldsChangedWhileSavingRef.current.clear();
      mrnChangedWhileSavingRef.current = false;
      takePendingSave(pendingSaveRef);
      if (mountedRef.current) {
        setSaveStatus('saving');
      }
      try {
        const body: PatientUpdate = {
          ...getChangedMrnUpdate(
            formSnapshot,
            lastSavedFormRef.current,
            explicitlyChangedMrn
          ),
          first_name: formSnapshot.first_name.trim(),
          last_name: formSnapshot.last_name.trim(),
          middle_name: formSnapshot.middle_name.trim() || null,
          suffix: formSnapshot.suffix.trim() || null,
          date_of_birth: formSnapshot.date_of_birth
            ? formatDateToISO(formSnapshot.date_of_birth)
            : null,
          gender: formSnapshot.gender || null,
          marital_status: formSnapshot.marital_status || null,
          employment_status: formSnapshot.employment_status || null,
          primary_language: formSnapshot.primary_language.trim() || null,
          ssn: formSnapshot.ssn.replace(/\D/g, '') || null,
          mbi: formSnapshot.mbi.trim() || null,
          ...getChangedPhoneUpdates(
            formSnapshot,
            lastSavedFormRef.current,
            explicitlyChangedPhoneFields
          ),
          email: formSnapshot.email.trim() || null,
          address: formSnapshot.address.trim() || null,
          city: formSnapshot.city.trim() || null,
          state: formSnapshot.state || null,
          zip_code: formSnapshot.zip_code.trim() || null,
          county: formSnapshot.county.trim() || null,
          emergency_contact: formSnapshot.emergency_contact.trim() || null,
          emergency_contact_relationship:
            formSnapshot.emergency_contact_relationship.trim() || null,
          secondary_contact: formSnapshot.secondary_contact.trim() || null,
          secondary_contact_relationship:
            formSnapshot.secondary_contact_relationship.trim() || null,
          needs_legal_representative: formSnapshot.needs_legal_representative,
          has_poa: formSnapshot.has_poa,
        };

        const mrnBeforeSave = lastSavedFormRef.current.mrn;

        const response = await updatePatient({
          path: { patient_id: savingPatientId },
          body,
        });

        if (patientIdRef.current !== savingPatientId) {
          return;
        }

        const savedMrn = response?.data?.mrn ?? '';
        if (
          response?.data &&
          'mrn' in body &&
          savedMrn &&
          savedMrn !== mrnBeforeSave
        ) {
          // The save deliberately left OASIS M0020 alone, so ask whether the
          // charts that still answer with the old MRN should follow. Gated on
          // this request having carried an MRN, not just on the response
          // differing: someone else changing the MRN while this tab sits open
          // would otherwise pop the offer on a save of the address, naming a
          // change this user did not make. Also skipped when the MRN was
          // cleared, since there is nothing to write into an answered M0020
          // except a blank, which would erase it.
          void offerM0020Sync(savingPatientId, savedMrn);
        }

        const responseIsStale =
          pendingSaveRef.current != null ||
          editGenerationRef.current !== savingGeneration;
        const applyToForm = mountedRef.current && !responseIsStale;

        if (response?.data) {
          // Echo when this save is current, or after unmount so a remount
          // hydrates from what was persisted. Skip the echo while still
          // mounted and typing so the header does not snap to an older value.
          if (applyToForm || !mountedRef.current) {
            onPatientUpdated(response.data);
          }
          if (!applyToForm) {
            return;
          }
          const canonical = patientToForm(response.data);
          lastSavedFormRef.current = canonical;
          skipAutosaveRef.current = true;
          setForm(canonical);
          setSaveStatus('saved');
          // Only fall back to idle if a newer edit hasn't already moved on.
          // scheduleStatusReset clears on unmount so jsdom never sees a late setState.
          scheduleStatusReset('saved', 2000);
        } else if (mountedRef.current) {
          setSaveStatus('error');
          showError(
            getUserFacingApiMessage(
              response,
              'Failed to save patient information'
            )
          );
          scheduleStatusReset('error', 3000);
        }
      } catch (err) {
        if (!mountedRef.current) {
          return;
        }
        if (patientIdRef.current !== savingPatientId) {
          return;
        }
        if (editGenerationRef.current !== savingGeneration) {
          return;
        }
        console.error('Failed to save patient information:', err);
        setSaveStatus('error');
        showError('Failed to save patient information');
        scheduleStatusReset('error', 3000);
      } finally {
        isSavingRef.current = false;
        const pending = takePendingSave(pendingSaveRef);
        if (pending && pending.patientId === patientIdRef.current) {
          await doSave(pending.form, pending);
        }
      }
    },
    [
      canEditPatientInfo,
      isDirty,
      offerM0020Sync,
      onPatientUpdated,
      scheduleStatusReset,
      showError,
    ]
  );

  // Keep the debounce effect off `doSave`'s identity. Parent re-renders used
  // to rebuild `onPatientUpdated` (an inline lambda), which rebuilt `doSave`,
  // re-ran this effect, and bumped generation over a still-valid in-flight
  // save — skipping `onPatientUpdated` and retriggering the debounce.
  const doSaveRef = useRef(doSave);
  doSaveRef.current = doSave;

  // Tab switch and patient navigation unmount this component. Flush the
  // latest snapshot so a debounce timer (or extra keystrokes during an
  // in-flight PUT) is not dropped, which would leave the parent stale and
  // let the next remount autosave overwrite the value already saved.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (statusResetTimer.current) {
        clearTimeout(statusResetTimer.current);
        statusResetTimer.current = null;
      }
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      const snapshot = latestFormRef.current;
      if (!snapshot.first_name.trim() || !snapshot.last_name.trim()) {
        return;
      }
      const dirty = formsDiffer(snapshot, lastSavedFormRef.current);
      if (isSavingRef.current) {
        pendingSaveRef.current = {
          patientId: patientIdRef.current,
          form: snapshot,
          force: !dirty,
          phoneFieldsChangedWhileSaving: Array.from(
            phoneFieldsChangedWhileSavingRef.current
          ),
          mrnChangedWhileSaving: mrnChangedWhileSavingRef.current,
        };
        return;
      }
      if (dirty) {
        void doSaveRef.current(snapshot);
      }
    };
  }, []);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    if (!canEditPatientInfo) {
      setSaveStatus('idle');
      return;
    }

    // Bump on every user edit, including a revert or a temporarily invalid
    // form, so an in-flight response cannot restore the abandoned value.
    editGenerationRef.current += 1;

    const hasRequiredNames = Boolean(
      form.first_name.trim() && form.last_name.trim()
    );
    const hasValidPhones = hasValidChangedPhones(
      form,
      lastSavedFormRef.current
    );
    const hasValidMrn = hasValidChangedMrn(form, lastSavedFormRef.current);
    const willSave =
      hasRequiredNames && hasValidPhones && hasValidMrn && isDirty(form);
    if (!willSave) {
      setSaveStatus(s => (s === 'unsaved' ? 'idle' : s));
      if (isSavingRef.current || pendingSaveRef.current != null) {
        pendingSaveRef.current = hasRequiredNames
          ? {
              patientId: patientIdRef.current,
              form,
              force: !isDirty(form),
              phoneFieldsChangedWhileSaving: Array.from(
                phoneFieldsChangedWhileSavingRef.current
              ),
              mrnChangedWhileSaving: mrnChangedWhileSavingRef.current,
            }
          : null;
      }
      return;
    }

    setSaveStatus('unsaved');
    setChangeToken(t => t + 1);

    debounceTimer.current = setTimeout(() => {
      doSaveRef.current(latestFormRef.current);
    }, CHART_AUTOSAVE_DELAY_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [canEditPatientInfo, form, isDirty]);

  // The values the export takes from Patient Info, as the save would send
  // them; judged as typed so a CMS fatal shows under its field (EMR-2710).
  const cmsCheck = useCmsRecordCheck(
    canEditPatientInfo
      ? {
          first_name: form.first_name.trim() || null,
          last_name: form.last_name.trim() || null,
          middle_name: form.middle_name.trim() || null,
          suffix: form.suffix.trim() || null,
          date_of_birth: form.date_of_birth
            ? formatDateToISO(form.date_of_birth)
            : null,
          ssn: form.ssn.replace(/\D/g, '') || null,
          state: form.state || null,
          zip_code: form.zip_code.trim() || null,
        }
      : null,
    async body =>
      (
        await checkPatientOasisExportErrors({
          path: { patient_id: patient.id },
          body,
        })
      ).data
  );
  const cmsErrors = cmsErrorsByField(cmsCheck?.errors ?? []);

  const updateField: UpdatePatientInfoField = (key, value) => {
    if (isSavingRef.current && isPhoneField(key)) {
      phoneFieldsChangedWhileSavingRef.current.add(key);
    }
    if (isSavingRef.current && key === 'mrn') {
      mrnChangedWhileSavingRef.current = true;
    }
    setForm(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className='space-y-6 max-w-4xl'>
      {/* Auto-save status pill: a zero-height sticky overlay that floats over
          the top-right of the form and stays in view while it scrolls. Zero
          height means showing/hiding the pill never shifts the layout. */}
      <div className='pointer-events-none sticky top-0 z-20 flex h-0 justify-end'>
        <AutoSavePill
          status={saveStatus}
          debounceMs={CHART_AUTOSAVE_DELAY_MS}
          changeToken={changeToken}
        />
      </div>

      <PatientDemographicSection
        form={form}
        updateField={updateField}
        canEditPatientInfo={canEditPatientInfo}
        cmsErrors={cmsErrors}
      />

      <PatientContactSection
        form={form}
        updateField={updateField}
        canEditPatientInfo={canEditPatientInfo}
        cmsErrors={cmsErrors}
      />

      <PatientEmergencyContactsSection
        form={form}
        updateField={updateField}
        canEditPatientInfo={canEditPatientInfo}
      />

      <PatientEpisodeFlagsSection
        patient={patient}
        canEditPatientInfo={canEditPatientInfo}
        onPatientUpdated={onPatientUpdated}
        onEpisodeAssignmentsChanged={onEpisodeAssignmentsChanged}
      />

      {canEditPatientInfo && (
        <PatientTagsSection
          patientId={patient.id}
          clinicId={patient.clinic_id}
          canEdit={canEditPatientInfo}
        />
      )}

      {/* Offered after an MRN change; declining leaves the charts as they are,
          and the OASIS review flags the mismatch until someone updates them */}
      <ConfirmationDialog
        open={!!m0020Sync}
        title='Update OASIS Patient ID?'
        message={m0020SyncMessage(m0020Sync)}
        confirmLabel='Yes, update OASIS'
        cancelLabel='No'
        loading={isSyncingM0020}
        onAccept={() => void handleConfirmM0020Sync()}
        onCancel={() => setM0020Sync(null)}
      />
    </div>
  );
};
